import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {HttpErrorResponse} from '@angular/common/http';
import {DataService} from '../../../Services/data.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import * as xlsx from 'xlsx';

// Target columns expected by the `entry/import` API, in template order.
interface TemplateField {
  key: string;
  label: string;
  required: boolean;
}

const TEMPLATE_FIELDS: TemplateField[] = [
  {key: 'date', label: 'Date', required: true},
  {key: 'description', label: 'Description', required: true},
  {key: 'reference', label: 'Reference', required: false},
  {key: 'company_name', label: 'Company Name', required: true},
  {key: 'account_number', label: 'Account Number', required: true},
  {key: 'debit', label: 'Debit', required: true},
  {key: 'credit', label: 'Credit', required: true},
  {key: 'line_description', label: 'Line Description', required: false},
  {key: 'entity_name', label: 'Entity Name', required: false},
  {key: 'location_name', label: 'Location Name', required: false},
  {key: 'create_reversal', label: 'Create Reversal', required: false},
];

@Component({
  selector: 'app-gl-import',
  templateUrl: './gl-import.component.html',
  styleUrls: ['./gl-import.component.css'],
  standalone: false
})
export class GlImportComponent implements OnInit {

  templateFields = TEMPLATE_FIELDS;

  vm: any = {
    // wizard step: 1=upload, 2=map columns, 3=review, 4=done
    step: 1,
    selectedFile: null,
    fileName: '',
    batchDescription: '',
    sourceSystem: '',
    uploading: false,
    importResult: null,
    showPreview: false,
    showCompanyIds: false,
    companies: [],
    copiedCompanyId: null,
    showEntityNames: false,
    entities: [],
    copiedEntityName: null,
    showLocationNames: false,
    locations: [],
    copiedLocationName: null,
    // column mapping state
    needsMapping: false,
    parsingFile: false,
    sourceHeaders: [] as string[],
    sourceRows: [] as any[][],
    skippedTitleRowCount: 0,
    columnMap: {} as { [fieldKey: string]: string },
    mappingError: '',
    accounts: [],
    accountOverrides: {} as { [sourceValue: string]: string },
    accountSearchTerms: {} as { [sourceValue: string]: string },
    skipZeroDebitCreditRows: false,
  };

  constructor(
    private router: Router,
    private titleService: Title,
    private tableDataService: DataService,
    private notification: BsToastService,
  ) {
  }

  ngOnInit(): void {
    this.titleService.setTitle('GL Journal Entry Import');
    this.loadCompanies();
    this.loadAccounts();
  }

  loadAccounts(): void {
    this.tableDataService.getApiListData('account', '?state=active&pageNumber=0&pageSize=999999').subscribe({
      next: (result: any) => {
        this.vm.accounts = result.Data || [];
      },
      error: () => {
        this.vm.accounts = [];
      }
    });
  }

  loadCompanies(): void {
    this.tableDataService.getApiListData('company', '?state=active&pageNumber=0&pageSize=999999').subscribe({
      next: (result: any) => {
        this.vm.companies = result.Data || [];
      },
      error: () => {
        this.vm.companies = [];
      }
    });
  }

  copyCompanyId(id: string): void {
    navigator.clipboard.writeText(id).then(() => {
      this.vm.copiedCompanyId = id;
      setTimeout(() => {
        this.vm.copiedCompanyId = null;
      }, 1500);
    });
  }

  loadEntities(): void {
    this.tableDataService.getApiListData('entity', '?state=active&pageNumber=0&pageSize=999999').subscribe({
      next: (result: any) => {
        this.vm.entities = result.Data || [];
      },
      error: () => {
        this.vm.entities = [];
      }
    });
  }

  copyEntityName(name: string): void {
    navigator.clipboard.writeText(name).then(() => {
      this.vm.copiedEntityName = name;
      setTimeout(() => {
        this.vm.copiedEntityName = null;
      }, 1500);
    });
  }

  loadLocations(): void {
    this.tableDataService.getApiListData('location', '?state=active&pageNumber=0&pageSize=999999').subscribe({
      next: (result: any) => {
        this.vm.locations = result.Data || [];
      },
      error: () => {
        this.vm.locations = [];
      }
    });
  }

  copyLocationName(name: string): void {
    navigator.clipboard.writeText(name).then(() => {
      this.vm.copiedLocationName = name;
      setTimeout(() => {
        this.vm.copiedLocationName = null;
      }, 1500);
    });
  }

  // ── File selection ─────────────────────────────────────────────────────────

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.vm.selectedFile = input.files[0];
      this.vm.fileName = input.files[0].name;
      this.vm.mappingError = '';
    }
  }

  get canUpload(): boolean {
    return !!this.vm.selectedFile;
  }

  // ── Column mapping ─────────────────────────────────────────────────────────

  /** Reads the selected file and moves to the mapping step. */
  mapColumns(): void {
    if (!this.canUpload) {
      return;
    }
    this.vm.parsingFile = true;
    this.vm.mappingError = '';
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const data = e.target.result;
        const workbook = xlsx.read(data, {type: 'array'});
        const firstSheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheetName];
        const rows: any[][] = xlsx.utils.sheet_to_json(sheet, {header: 1, raw: false, defval: ''});
        const nonEmptyRows = rows.filter(r => r && r.some(cell => String(cell ?? '').trim() !== ''));
        if (!nonEmptyRows.length) {
          this.vm.mappingError = 'The selected file appears to be empty.';
          this.vm.parsingFile = false;
          return;
        }
        // Some source files start with one or more "title" rows (e.g. a report caption
        // occupying a single cell) before the real header row. Treat the first row that
        // has more than one populated cell as the header row, skipping any single-cell
        // rows above it.
        let headerRowIndex = nonEmptyRows.findIndex(r =>
          r.filter(cell => String(cell ?? '').trim() !== '').length > 1
        );
        if (headerRowIndex < 0) {
          headerRowIndex = 0;
        }
        this.vm.skippedTitleRowCount = headerRowIndex;
        this.vm.sourceHeaders = (nonEmptyRows[headerRowIndex] || []).map((h: any) => String(h ?? '').trim());
        this.vm.sourceRows = nonEmptyRows.slice(headerRowIndex + 1);
        this.autoMapColumns();
        this.vm.parsingFile = false;
        this.vm.step = 2;
      } catch (err) {
        this.vm.parsingFile = false;
        this.vm.mappingError = 'Unable to read this file. Please confirm it is a valid CSV or XLSX file.';
      }
    };
    reader.onerror = () => {
      this.vm.parsingFile = false;
      this.vm.mappingError = 'Unable to read this file. Please confirm it is a valid CSV or XLSX file.';
    };
    reader.readAsArrayBuffer(this.vm.selectedFile);
  }

  /**
   * Pre-fills the mapping by matching source headers to template fields by name. Preserves
   * any mapping the user already made (e.g. from a prior attempt on a re-uploaded, corrected
   * file) — only fields that aren't already mapped are auto-matched against the new headers.
   */
  private autoMapColumns(): void {
    const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const map: { [fieldKey: string]: string } = {...this.vm.columnMap};
    for (const field of this.templateFields) {
      if (map[field.key] && this.vm.sourceHeaders.includes(map[field.key])) {
        continue;
      }
      const match = this.vm.sourceHeaders.find((h: string) =>
        normalize(h) === normalize(field.key) || normalize(h) === normalize(field.label)
      );
      map[field.key] = match || '';
    }
    this.vm.columnMap = map;
  }

  get mappingComplete(): boolean {
    return this.templateFields
      .filter(f => f.required)
      .every(f => !!this.vm.columnMap[f.key]);
  }

  private isZeroAmount(raw: any): boolean {
    const str = String(raw ?? '').trim();
    if (str === '') {
      return true;
    }
    const num = Number(str.replace(/[,$()]/g, ''));
    return !isNaN(num) && num === 0;
  }

  private isSkippableRow(row: any[]): boolean {
    if (!this.vm.skipZeroDebitCreditRows) {
      return false;
    }
    const debitHeader = this.vm.columnMap['debit'];
    const creditHeader = this.vm.columnMap['credit'];
    const debitIndex = debitHeader ? this.vm.sourceHeaders.indexOf(debitHeader) : -1;
    const creditIndex = creditHeader ? this.vm.sourceHeaders.indexOf(creditHeader) : -1;
    if (debitIndex < 0 || creditIndex < 0) {
      return false;
    }
    return this.isZeroAmount(row[debitIndex]) && this.isZeroAmount(row[creditIndex]);
  }

  /** Distinct account_number values present in the source file that don't match any known account. */
  get unmatchedAccountNumbers(): string[] {
    const sourceHeader = this.vm.columnMap['account_number'];
    if (!sourceHeader || !this.vm.accounts?.length) {
      return [];
    }
    const colIndex = this.vm.sourceHeaders.indexOf(sourceHeader);
    if (colIndex < 0) {
      return [];
    }
    const knownNumbers = new Set(this.vm.accounts.map((a: any) => String(a.number).trim()));
    const seen = new Set<string>();
    for (const row of this.vm.sourceRows) {
      if (this.isSkippableRow(row)) {
        continue;
      }
      const raw = String(row?.[colIndex] ?? '').trim();
      if (!raw || knownNumbers.has(raw)) {
        continue;
      }
      seen.add(raw);
    }
    return Array.from(seen).sort();
  }

  get hasUnmatchedAccounts(): boolean {
    return this.unmatchedAccountNumbers.length > 0;
  }

  get accountOverridesComplete(): boolean {
    return this.unmatchedAccountNumbers.every(val => !!this.vm.accountOverrides[val]);
  }

  /** Accounts matching the current search term for a given unmatched source value (number or name, case-insensitive). Simple typeahead over the already-loaded account list. */
  filteredAccountsFor(val: string): any[] {
    const term = (this.vm.accountSearchTerms[val] || '').toString().trim().toLowerCase();
    const accounts: any[] = this.vm.accounts || [];
    if (!term) {
      return accounts;
    }
    return accounts.filter((a: any) =>
      String(a.number ?? '').toLowerCase().includes(term) ||
      String(a.name ?? '').toLowerCase().includes(term)
    );
  }

  /** Whether the account list box should be shown open (expanded) for this unmatched value — true once the typed filter has at least one match. */
  accountListOpenFor(val: string): boolean {
    const term = (this.vm.accountSearchTerms[val] || '').toString().trim();
    return !!term && this.filteredAccountsFor(val).length > 0;
  }

  /** Number of visible rows for the expanded account list box, capped to keep it from growing too tall. */
  accountListSizeFor(val: string): number {
    return Math.min(Math.max(this.filteredAccountsFor(val).length, 2), 8);
  }

  /** Closes the expanded account list box after the user picks an account. */
  onAccountOverrideSelected(val: string): void {
    this.vm.accountSearchTerms[val] = '';
  }

  backToUpload(): void {
    this.vm.step = 1;
  }

  /** Builds a new CSV file in the required template layout from the user's mapping, then validates. */
  applyMappingAndValidate(): void {
    if (!this.mappingComplete || !this.accountOverridesComplete) {
      return;
    }
    const csv = this.buildMappedCsv();
    const baseName = (this.vm.fileName || 'import').replace(/\.[^.]+$/, '');
    const mappedFile = new File([csv], `${baseName}-mapped.csv`, {type: 'text/csv'});
    this.vm.selectedFile = mappedFile;
    this.vm.fileName = mappedFile.name;
    this.validate();
  }

  private buildMappedCsv(): string {
    const escape = (value: any): string => {
      const str = value === null || value === undefined ? '' : String(value);
      if (/[",\n]/.test(str)) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };

    const headerLine = this.templateFields.map(f => escape(f.key)).join(',');
    const lines = [headerLine];

    for (const row of this.vm.sourceRows) {
      // Skip fully blank rows, but preserve blank separator rows between entries.
      const isBlank = !row || row.every((cell: any) => String(cell ?? '').trim() === '');
      if (isBlank) {
        lines.push('');
        continue;
      }
      if (this.isSkippableRow(row)) {
        continue;
      }
      const outRow = this.templateFields.map(field => {
        const sourceHeader = this.vm.columnMap[field.key];
        if (!sourceHeader) {
          return '';
        }
        const colIndex = this.vm.sourceHeaders.indexOf(sourceHeader);
        let value = colIndex >= 0 ? row[colIndex] : '';
        if (field.key === 'account_number') {
          const raw = String(value ?? '').trim();
          const override = this.vm.accountOverrides[raw];
          if (override) {
            value = override;
          }
        }
        return escape(value);
      });
      lines.push(outRow.join(','));
    }
    return lines.join('\n');
  }

  // ── Validate (dry run) ─────────────────────────────────────────────────────

  validate(): void {
    if (!this.canUpload) {
      return;
    }
    this.vm.uploading = true;
    this.tableDataService.postFormDataSilent('entry/import', this.buildForm(true)).subscribe({
      next: (result: any) => {
        this.vm.uploading = false;
        this.vm.importResult = result;
        this.vm.step = 3;
      },
      error: (err: HttpErrorResponse) => {
        this.vm.uploading = false;
        this.notification.showErrorToast(err.error?.message || 'Validation failed. Please check the file and try again.');
      }
    });
  }

  // ── Confirm import ─────────────────────────────────────────────────────────

  confirmImport(): void {
    this.vm.uploading = true;
    this.tableDataService.postFormData('entry/import', this.buildForm(false)).subscribe({
      next: (result: any) => {
        this.vm.uploading = false;
        this.vm.importResult = result;
        this.vm.step = 4;
      },
      error: (err: HttpErrorResponse) => {
        this.vm.uploading = false;
        this.notification.showErrorToast(err.error?.message || 'Import failed. Please check the file and try again.');
      }
    });
  }

  private buildForm(dryRun: boolean): FormData {
    const form = new FormData();
    form.append('file', this.vm.selectedFile, this.vm.fileName);
    form.append('dry_run', String(dryRun));
    if (this.vm.batchDescription?.trim()) {
      form.append('batch_description', this.vm.batchDescription);
    }
    if (this.vm.sourceSystem?.trim()) {
      form.append('source_system', this.vm.sourceSystem);
    }
    return form;
  }

  // ── Navigation ─────────────────────────────────────────────────────────────

  startOver(): void {
    this.vm.step = 1;
    this.vm.selectedFile = null;
    this.vm.fileName = '';
    this.vm.batchDescription = '';
    this.vm.sourceSystem = '';
    this.vm.importResult = null;
    this.vm.showPreview = false;
    this.vm.copiedCompanyId = null;
    this.vm.copiedEntityName = null;
    this.vm.copiedLocationName = null;
    this.vm.sourceHeaders = [];
    this.vm.sourceRows = [];
    this.vm.skippedTitleRowCount = 0;
    this.vm.columnMap = {};
    this.vm.mappingError = '';
    this.vm.accountOverrides = {};
    this.vm.accountSearchTerms = {};
    this.vm.skipZeroDebitCreditRows = false;
  }

  /**
   * Goes back to the upload step after a failed validation, but — unlike `startOver()` —
   * keeps the column mapping, account overrides, batch description, source system, and
   * skip-zero-rows setting intact. This lets the user fix the source file and re-upload it
   * without re-choosing the same mapping/settings they already set up. Only the selected
   * file, parsed rows/headers, and the previous (failed) validation result are cleared,
   * since those belong to the file being replaced.
   */
  reUploadKeepSettings(): void {
    this.vm.step = 1;
    this.vm.selectedFile = null;
    this.vm.fileName = '';
    this.vm.importResult = null;
    this.vm.sourceHeaders = [];
    this.vm.sourceRows = [];
    this.vm.mappingError = '';
  }

  goToEntries(): void {
    this.router.navigate(['/transactions/entry/list']).then();
  }

  // ── Download template ──────────────────────────────────────────────────────

  downloadTemplate(): void {
    const link = document.createElement('a');
    link.href = 'assets/templates/gl-import-template.csv';
    link.download = 'gl-import-template.csv';
    link.click();
  }

  // ── Result helpers ─────────────────────────────────────────────────────────

  get results(): any[] {
    return this.vm.importResult?.results || [];
  }

  get hasErrors(): boolean {
    return !!this.vm.importResult?.has_errors;
  }

  entryRowClass(entry: any): string {
    return '';
  }
}
